import React, { useEffect, useState } from "react";
import FuturesLeverageSlider from "../futures_trade/FuturesLeverageSlider";

/**
 * Margin — “Adjust Leverage” popup.
 * Styling lives in `margin.css` (classes prefixed `agce-margin-lvg-`).
 */
export default function MarginLeverageModal({
  open,
  onClose,
  onConfirm,
  coinLabel,
  coinIconSrc,
  leverage,
  onChangeLeverage,
  allowedLeverages,
  minLeverage = 1,
  maxLeverage = 20,
  netEquity = 0,
  currentLoan = 0,
  quoteSymbol = "",
  formatTotal,
}) {
  const fmt = (n) => (formatTotal ? formatTotal(n) : Number(n).toFixed(2));
  const [leverageError, setLeverageError] = useState("");

  useEffect(() => { setLeverageError(""); }, [leverage]);

  const allowed = React.useMemo(() => {
    let min = minLeverage;
    let max = maxLeverage;
    if (Array.isArray(allowedLeverages) && allowedLeverages.length > 0) {
      const nums = [...new Set(allowedLeverages.map(Number).filter((n) => Number.isFinite(n) && n > 0))]
        .sort((a, b) => a - b);
      if (nums.length > 0) return nums;
    }
    min = Math.max(1, Math.round(min));
    max = Math.max(min, Math.round(max));
    const arr = [];
    for (let i = min; i <= max; i += 1) {
      arr.push(i);
    }
    return arr;
  }, [allowedLeverages, minLeverage, maxLeverage]);

  const min = allowed[0];
  const max = allowed[allowed.length - 1];

  const clamp = (n) => {
    const x = Number(n);
    if (!allowed.length) return minLeverage;
    if (!Number.isFinite(x)) return allowed[0];
    if (allowed.includes(Math.round(x))) return Math.round(x);
    return allowed.reduce((best, v) => (Math.abs(v - x) < Math.abs(best - x) ? v : best), allowed[0]);
  };

  useEffect(() => {
    if (!open || !allowed.length) return;
    if (!allowed.includes(leverage)) {
      onChangeLeverage?.(clamp(leverage));
    }
  }, [open, allowed, leverage, onChangeLeverage]);

  const validate = (lev) => {
    const n = Number(lev);
    if (!Number.isFinite(n) || n <= 0) return "Please enter a valid leverage value.";
    if (allowed.length && !allowed.includes(Math.round(n))) {
      return `Allowed leverage: ${allowed.map((a) => `${a}x`).join(", ")}.`;
    }
    if (n < min) return `Minimum leverage is ${min}x.`;
    if (n > max) return `Maximum leverage is ${max}x.`;
    return "";
  };

  const handleClose = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    onClose?.();
  };

  const handleConfirm = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const final = clamp(leverage);
    const err = validate(final);
    if (err) { setLeverageError(err); return; }
    onConfirm?.(final);
    onClose?.();
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const current = clamp(leverage);
  const maxOpen = fmt(netEquity * current);

  return (
    <div className="agce-margin-lvg-modal-root" role="presentation">
      <div className="agce-margin-lvg-modal-backdrop" onClick={handleClose} aria-hidden />
      <div
        className="agce-margin-lvg-modal-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="agce-margin-lvg-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="agce-margin-lvg-modal-head">
          <h2 id="agce-margin-lvg-title" className="agce-margin-lvg-modal-title">
            Adjust Leverage
          </h2>
          <button type="button" className="agce-margin-lvg-modal-close" onClick={handleClose} aria-label="Close">
            <i className="ri-close-line ri-lg" aria-hidden />
          </button>
        </div>
        <div className="agce-margin-lvg-modal-body">
          <span className="agce-margin-lvg-field-label">Coin</span>
          <div className="agce-margin-lvg-coin-row">
            {coinIconSrc ? <img src={coinIconSrc} alt="" width={24} height={24} /> : null}
            <span>{coinLabel || "—/—"}</span>
          </div>

          <span className="agce-margin-lvg-field-label">Leverage</span>
          <FuturesLeverageSlider
            value={current}
            onChange={onChangeLeverage}
            minLeverage={min}
            maxLeverage={max}
            allowedValues={allowed}
          />
          {leverageError && (
            <p style={{ margin: "4px 0 8px", fontSize: 12, color: "#ef4444" }}>{leverageError}</p>
          )}

          <ul className="agce-margin-lvg-notes">
            <li>
              Maximum position at current leverage: {maxOpen} {quoteSymbol}
            </li>
            <li>
              Please note that leverage changing will also apply for open positions and open orders.
            </li>
            <li>
              Selecting higher leverage increases your liquidation risk.
              Always manage your risk levels.
            </li>
          </ul>

          {netEquity <= 0 && (
            <p className="agce-margin-lvg-warn">
              The current available margin ≤ 0. You can increase the leverage or add margin.
            </p>
          )}
        </div>
        <div className="agce-margin-lvg-modal-foot">
          <button type="button" className="agce-margin-lvg-btn agce-margin-lvg-btn--ghost" onClick={handleClose}>
            Cancel
          </button>
          <button
            type="button"
            className="agce-margin-lvg-btn agce-margin-lvg-btn--primary"
            onClick={handleConfirm}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}
